import { DirectionsCarOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import { ReactNode } from "react";

type NotificationCardDetail = {
    key: string;
    icon: ReactNode;
    text: string;
};

type NotificationCardProps = {
    category: string;
    severity: string;
    severityColor: "error" | "warning" | "info";
    title: string;
    subject: string;
    subjectIcon?: ReactNode;
    message?: string;
    details?: NotificationCardDetail[];
};

const severityStyles = {
    error: {
        border: "!tw-border-l-red-500",
        chip: "!tw-border-red-200 !tw-bg-red-50 !tw-text-red-700 dark:!tw-border-red-800 dark:!tw-bg-red-950/60 dark:!tw-text-red-200",
        marker: "tw-bg-red-500",
    },
    warning: {
        border: "!tw-border-l-amber-500",
        chip: "!tw-border-amber-200 !tw-bg-amber-50 !tw-text-amber-700 dark:!tw-border-amber-800 dark:!tw-bg-amber-950/60 dark:!tw-text-amber-200",
        marker: "tw-bg-amber-500",
    },
    info: {
        border: "!tw-border-l-sky-500",
        chip: "!tw-border-sky-200 !tw-bg-sky-50 !tw-text-sky-700 dark:!tw-border-sky-800 dark:!tw-bg-sky-950/60 dark:!tw-text-sky-200",
        marker: "tw-bg-sky-500",
    },
} as const;

const NotificationCard = ({
    category,
    severity,
    severityColor,
    title,
    subject,
    subjectIcon = <DirectionsCarOutlined fontSize="small" color="primary" />,
    message,
    details = [],
}: NotificationCardProps) => {
    const visualStyle = severityStyles[severityColor];

    return (
        <Card
            component="article"
            variant="outlined"
            className={`notification-alert-card tw-group tw-relative tw-w-full tw-min-w-0 !tw-overflow-hidden !tw-rounded-xl !tw-border !tw-border-slate-200 !tw-border-l-4 tw-bg-white tw-shadow-none tw-transition-colors tw-duration-200 hover:tw-border-slate-300 dark:!tw-bg-slate-900 ${visualStyle.border}`}
        >
            <CardContent className="!tw-p-3 sm:!tw-p-4 [&:last-child]:!tw-pb-3 sm:[&:last-child]:!tw-pb-4">
                <Box className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                    <Chip
                        label={category}
                        size="small"
                        variant="outlined"
                        className="!tw-max-w-[70%] !tw-border-slate-200 !tw-bg-slate-50 !tw-font-prometo !tw-font-bold !tw-text-gtr-teal-dark dark:!tw-border-slate-700 dark:!tw-bg-slate-800 dark:!tw-text-slate-200"
                    />
                    <Chip
                        label={severity}
                        size="small"
                        variant="outlined"
                        className={`!tw-font-prometo !tw-font-bold ${visualStyle.chip}`}
                    />
                </Box>

                <Typography variant="subtitle2" className="!tw-mt-3 !tw-break-words !tw-font-prometo !tw-font-bold !tw-leading-snug !tw-text-slate-800 dark:!tw-text-slate-100">
                    {title}
                </Typography>

                <Box
                    className="tw-mt-2.5 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-gtr-teal/5 tw-px-2.5 tw-py-2 dark:tw-bg-slate-800"
                    sx={{
                        "& .MuiSvgIcon-root": { color: "#006C77" },
                    }}
                >
                    <Box className="tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-shadow-sm dark:tw-bg-slate-700">
                        {subjectIcon}
                    </Box>
                    <Typography variant="body2" className="!tw-min-w-0 !tw-break-words !tw-font-prometo !tw-font-bold !tw-text-slate-700 dark:!tw-text-slate-200">
                        {subject}
                    </Typography>
                </Box>

                {message && (
                    <Typography variant="body2" className="!tw-mt-2.5 !tw-break-words !tw-font-prometo !tw-leading-relaxed !tw-text-slate-600 dark:!tw-text-slate-300">
                        {message}
                    </Typography>
                )}

                {details.length > 0 && (
                    <Box className="tw-mt-3 tw-divide-y tw-divide-slate-100 tw-rounded-lg tw-bg-slate-50/60 tw-px-2.5 dark:tw-divide-slate-700 dark:tw-bg-slate-950/60">
                        {details.map((detail) => (
                            <Box
                                key={detail.key}
                                className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-py-2 first:tw-pt-2.5 last:tw-pb-2.5"
                                sx={{ "& .MuiSvgIcon-root": { color: "#64748b" } }}
                            >
                                <span className={`tw-h-1.5 tw-w-1.5 tw-flex-none tw-rounded-full ${visualStyle.marker}`} />
                                <Box className="tw-flex tw-flex-none tw-items-center">
                                    {detail.icon}
                                </Box>
                                <Typography variant="caption" className="!tw-min-w-0 !tw-break-words !tw-font-prometo !tw-leading-relaxed !tw-text-slate-500 dark:!tw-text-slate-300">
                                    {detail.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default NotificationCard;
